import { useMemo, useState } from 'react'

import { match } from 'ts-pattern'

import {
  Box,
  Button,
  DatePickerField,
  Flex,
  PriceField,
  TextInputField,
  Totals,
  Modal,
  SimpleModal
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { Invoice } from 'Invoices/types'

import { InvoiceSection } from '../IssueCreditNote/InvoiceSection'
import { useRecordPayment } from './useRecordPayment'
import { PAYMENT_REFERENCE_MAX_LENGTH } from './recordPayment.constants'
import {
  canSubmitPaymentAmount,
  clampToZero,
  getInvoiceBalanceDetails,
  getPaymentAmountFeedback,
  isPositiveAmount,
  negateAmount,
  parseAmount,
  parseReference,
  subtractAmounts,
  toPaymentDateIso
} from './recordPayment.helpers'

/**
 * Drawer for recording a manual payment against an issued invoice. Mounted
 * while open, so the form re-seeds from the invoice on each open. Loads the
 * invoice from the query cache, depending only on an invoiceId.
 */
export const RecordPaymentDrawer = ({
  invoiceId,
  onClose
}: {
  invoiceId: string
  onClose: () => void
}) => (
  <Modal
    isOpen
    onClose={onClose}
    variant="drawer"
    width="620px"
    data-testid="record-payment.drawer"
  >
    <Modal.Header showCloseButton withBorder>
      Record payment
    </Modal.Header>
    <DrawerBodyContent invoiceId={invoiceId} onClose={onClose} />
  </Modal>
)

const DrawerBodyContent = ({
  invoiceId,
  onClose
}: {
  invoiceId: string
  onClose: () => void
}) => {
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId },
    { staleTime: 1 * MINUTES }
  )

  const invoice = invoiceQuery.data

  if (!invoice) {
    return null
  }

  return <DrawerForm invoice={invoice} onClose={onClose} />
}

const DrawerForm = ({
  invoice,
  onClose
}: {
  invoice: Invoice
  onClose: () => void
}) => {
  const { currency, grossTotal, netTotal, totalTax } = invoice

  const { credited, priorPaid, totalDue, balance } =
    getInvoiceBalanceDetails(invoice)

  const { recordPayment } = useRecordPayment({ invoiceId: invoice.id })

  const [amount, setAmount] = useState(() => balance)
  const [date, setDate] = useState(() => new Date())
  const [reference, setReference] = useState('')
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isAmountFocused, setIsAmountFocused] = useState(false)

  const parsedAmount = parseAmount(amount)
  const feedback = getPaymentAmountFeedback(parsedAmount, balance)
  const canSubmit = canSubmitPaymentAmount(feedback)

  // Zero error waits until focus leaves; erasing the seed passes through '.00'.
  // Live focus, not a sticky flag: the focus lock bounces through this field on
  // open, and that blur is not the user leaving it.
  //
  //   '1,620.00' -> '620.00' -> '.00'   (zero, focused: quiet)
  //   '2,000.00'                        (over balance: flagged live)
  const amountError = match(feedback)
    .with({ type: 'nonPositive' }, () =>
      isAmountFocused ? null : 'Enter an amount greater than zero'
    )
    .with(
      { type: 'exceeds' },
      () =>
        `Amount cannot exceed the balance due of ${toMoney({
          value: balance,
          currency
        })}`
    )
    .otherwise(() => null)

  const amountValidationErrors = amountError ? [{ message: amountError }] : []

  // Itemised breakdown: the invoice total, less credit notes to reach the total
  // due, less payments already made and this payment, leaving what's remaining.
  // Only a valid amount counts toward this payment.
  const currentPayment = canSubmit && parsedAmount ? parsedAmount : '0.00'
  const remaining = clampToZero(
    subtractAmounts(totalDue, priorPaid, currentPayment)
  )

  const invoiceReference = invoice.invoiceNumber ?? 'this invoice'

  // One key per drawer session, so retrying an ambiguous failure dedupes.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [])

  const onConfirm = async (): Promise<boolean> => {
    if (parsedAmount === null) return false

    const recorded = await recordPayment({
      amount: parsedAmount,
      currency,
      paymentDate: toPaymentDateIso(date),
      invoiceBalanceVersion: invoice.balanceVersion ?? 0,
      idempotencyKey,
      reference: parseReference(reference)
    })

    if (recorded) {
      onClose()
    }

    return recorded
  }

  return (
    <>
      <Modal.Content>
        <Flex flexDir="column" gap="20px">
          <InvoiceSection invoice={invoice} />

          <PriceField
            label="Amount paid"
            variant="v2"
            currency={currency}
            value={amount}
            validationErrors={amountValidationErrors}
            onChange={valueAsString => setAmount(valueAsString)}
            onFocus={() => setIsAmountFocused(true)}
            onBlur={() => setIsAmountFocused(false)}
            data-testid="record-payment.amount"
          />

          <DatePickerField
            label="Payment date"
            value={date}
            onChange={newDate => newDate && setDate(newDate)}
            placeholder="Select date"
            width="100%"
            data-testid="record-payment.date"
          />

          <TextInputField
            label="Reference (optional)"
            value={reference}
            onChange={event => setReference(event.target.value)}
            placeholder="Reference"
            maxLength={PAYMENT_REFERENCE_MAX_LENGTH}
            data-testid="record-payment.reference"
          />

          <Flex flexDir="column" alignItems="flex-end" gap="8px">
            <Box width="320px">
              <Totals currency={currency}>
                <Totals.Row title="Subtotal" amount={netTotal} />
                <Totals.Row title="Tax" amount={totalTax} />
                <Totals.Divider />
                <Totals.Row title="Total" amount={grossTotal} />
                {isPositiveAmount(credited) && (
                  <>
                    <Totals.Row
                      title="Credited"
                      amount={negateAmount(credited)}
                    />
                    <Totals.Divider />
                    <Totals.Row title="Total due" amount={totalDue} />
                  </>
                )}
                {isPositiveAmount(priorPaid) && (
                  <Totals.Row
                    title="Previously paid"
                    amount={negateAmount(priorPaid)}
                  />
                )}
                <Totals.Row
                  title="This payment"
                  amount={negateAmount(currentPayment)}
                />
                <Totals.Divider />
                <Totals.Total title="Amount remaining" amount={remaining} />
              </Totals>
            </Box>
          </Flex>
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        {/* CancelButton routes through the modal's animated close, so the exit
            transition plays before the conditionally-mounted drawer unmounts. */}
        <Modal.CancelButton data-testid="record-payment.cancel" />
        <Button
          variant="primary"
          onClick={() => setIsConfirmOpen(true)}
          disabled={!canSubmit}
          data-testid="record-payment.submit"
        >
          Record payment
        </Button>
      </Modal.Footer>
      <SimpleModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        variant="dialog"
        title="Record payment"
        submitButtonText="Record payment"
        submittingButtonText="Recording payment..."
        onSubmit={onConfirm}
        data-testid="record-payment.confirm"
      >
        A payment of {toMoney({ value: parsedAmount ?? '0.00', currency })} will
        be applied to {invoiceReference}.
      </SimpleModal>
    </>
  )
}
